import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Button, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import PartnerAuthLayout from "layouts/authentication/components/PartnerAuthLayout";
import ForgetPassword from "redux/Thunks/Authentication";
import Validations from "utils/Validations/index";
import constant from "utils/Constants";

function ForgotPassword() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleValidation = () => {
    const emailValidate = Validations.validate("email", email, null, null, true);
    if (emailValidate !== "") {
      setEmailError(emailValidate);
      return false;
    }
    return true;
  };

  const handleForgetPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);

    const val = handleValidation();
    if (val) {
      const res = await dispatch(ForgetPassword({ email }));
      if (res.payload.status === 200) {
        setStatus(true);
        setError("");
      } else if (res.payload.status === 400) {
        setError(constant.FORGET_PASSWORD_EMAIL_Failure);
      } else {
        setError(res.payload.data?.message || constant.FORGET_PASSWORD_EMAIL_Failure);
      }
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
    setError("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleForgetPassword(event);
    }
  };

  return (
    <PartnerAuthLayout>
      {/* Heading */}
      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{
            fontSize: { xs: "24px", md: "28px" },
            fontWeight: 800,
            color: "#101828",
            fontFamily: "Inter, sans-serif",
            letterSpacing: "-0.02em",
          }}
        >
          Forgot <span style={{ color: "#1E40AF" }}>Password!</span>
        </Typography>
        <Typography sx={{ fontSize: "13.5px", color: "#64748B", mt: 0.5 }}>
          {status
            ? "Check your email for the password reset link"
            : "Enter your register email and we'll send you a password reset link"}
        </Typography>
      </Box>

      {status ? (
        <>
          {/* Success Notification */}
          <Box
            sx={{
              backgroundColor: "#F0FDF4",
              border: "1px solid #DCFCE7",
              color: "#166534",
              px: 2,
              py: 1.5,
              borderRadius: "8px",
              mb: 2.5,
              fontSize: "13px",
            }}
          >
            {constant.FORGET_PASSWORD_MESSAGE ||
              "Password reset link has been sent to your registered email."}
          </Box>

          {/* Back to Log In */}
          <Box sx={{ mt: 4, mb: 1, textAlign: "center" }}>
            <Typography
              component={Link}
              to="/authentication/sign-in"
              sx={{
                color: "#1E40AF",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: "18px" }} /> Back to Log In
            </Typography>
          </Box>
        </>
      ) : (
        <>
          {/* Error Notification */}
          {error ? (
            <Box
              sx={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FEE2E2",
                color: "#991B1B",
                px: 2,
                py: 1.5,
                borderRadius: "8px",
                mb: 2.5,
                fontSize: "13px",
              }}
            >
              {error}
            </Box>
          ) : null}

          {/* Form */}
          <Box component="form" role="form" onSubmit={handleForgetPassword}>
            {/* Email Input */}
            <Box sx={{ mb: 1 }}>
              <Typography
                component="label"
                htmlFor="email"
                sx={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#344054",
                  mb: 0.6,
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                type="email"
                placeholder="Enter Your Email"
                value={email}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error={Boolean(emailError)}
                helperText={emailError}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#D0D5DD" },
                    "&:hover fieldset": { borderColor: "#98A2B3" },
                    "&.Mui-focused fieldset": { borderColor: "#1E40AF", borderWidth: "1.5px" },
                  },
                }}
                FormHelperTextProps={{ sx: { ml: 0, mt: 0.5, fontSize: "12px", color: "#DC2626" } }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              onClick={handleForgetPassword}
              sx={{
                mt: 3,
                backgroundColor: "#1E40AF",
                color: "#FFFFFF",
                height: "46px",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1D3A8A",
                  boxShadow: "none",
                },
                "&:disabled": {
                  backgroundColor: "#93C5FD",
                  color: "#FFFFFF",
                },
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>

            {/* Back to Log In */}
            <Box sx={{ mt: 4, mb: 1, textAlign: "center" }}>
              <Typography
                component={Link}
                to="/authentication/sign-in"
                sx={{
                  color: "#1E40AF",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: "18px" }} /> Back to Log In
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </PartnerAuthLayout>
  );
}

export default ForgotPassword;
